// src/components/Eventos/EventsHeats/utils/trackDistances.js

/**
 * Configuraciones de distancias por tipo de evento
 */
const TRACK_DISTANCE_CONFIGS = {
    // Eventos de medio fondo
    '800': [400, 800], // 400m, 800m
    '1500': [300, 700, 1100, 1500], // 300m, 700m, 1100m, 1500m
    '3000': [200, 400, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000, 2200, 2400, 2600, 2800, 3000],
    
    // Eventos de fondo
    '5000': (() => {
      const distances = [];
      for (let i = 200; i <= 5000; i += 200) {
        distances.push(i);
      }
      return distances;
    })(), // Cada 200m hasta 5000m
    
    '10000': (() => {
      const distances = [];
      for (let i = 400; i <= 10000; i += 400) {
        distances.push(i);
      }
      return distances;
    })(), // Cada 400m hasta 10000m
    
    // Eventos de obstáculos
    '3000SC': [200, 400, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000, 2200, 2400, 2600, 2800, 3000],
    
    // Relevo
    '4x400': [400, 800, 1200, 1600],
    '4x800': [800, 1600, 2400, 3200],
    
    // Milla y eventos especiales
    'MILE': [400, 800, 1200, 1609], // 1609m = 1 milla
    'HORA': (() => {
      const distances = [];
      for (let i = 400; i <= 20000; i += 400) {
        distances.push(i);
      }
      return distances;
    })(), // Cada 400m para carrera de hora
  };
  
  /**
   * Obtiene las distancias de splits para un evento específico
   * @param {string} eventCode - Código del evento (ej: '1500', '5000', etc.)
   * @param {number} totalDistance - Distancia total del evento (backup)
   * @returns {Array<number>} - Array de distancias para cada split
   */
  export const getEventSplitDistances = (eventCode, totalDistance = null) => {
    // Limpiar el código del evento (remover espacios, convertir a mayúsculas)
    const cleanCode = String(eventCode).trim().toUpperCase();
    
    // Buscar configuración exacta
    if (TRACK_DISTANCE_CONFIGS[cleanCode]) {
      return TRACK_DISTANCE_CONFIGS[cleanCode];
    }
    
    // Buscar configuraciones que contengan el código
    for (const [key, config] of Object.entries(TRACK_DISTANCE_CONFIGS)) {
      if (cleanCode.includes(key) || key.includes(cleanCode)) {
        return config;
      }
    }
    
    // Si se proporciona distancia total, generar splits automáticamente
    if (totalDistance && typeof totalDistance === 'number') {
      return generateAutomaticSplits(totalDistance);
    }
    
    // Fallback: generar splits de 400m
    console.warn(`[getEventSplitDistances] No se encontró configuración para evento: ${eventCode}`);
    return [400, 800, 1200, 1600]; // Fallback genérico
  };
  
  /**
   * Genera splits automáticos basados en la distancia total
   * @param {number} totalDistance - Distancia total del evento
   * @returns {Array<number>} - Array de distancias para splits
   */
  const generateAutomaticSplits = (totalDistance) => {
    const splits = [];
    
    if (totalDistance <= 800) {
      // Eventos cortos: splits cada 200m
      for (let i = 200; i <= totalDistance; i += 200) {
        splits.push(i);
      }
    } else if (totalDistance <= 1600) {
      // Eventos medios: splits cada 400m
      for (let i = 400; i <= totalDistance; i += 400) {
        splits.push(i);
      }
    } else if (totalDistance <= 3000) {
      // Eventos medio-fondo: splits cada 200m o 400m según la distancia
      const interval = totalDistance <= 2000 ? 200 : 400;
      for (let i = interval; i <= totalDistance; i += interval) {
        splits.push(i);
      }
    } else {
      // Eventos de fondo: splits cada 400m o 1000m según la distancia
      const interval = totalDistance <= 10000 ? 400 : 1000;
      for (let i = interval; i <= totalDistance; i += interval) {
        splits.push(i);
      }
    }
    
    return splits;
  };
  
  /**
   * Obtiene la distancia para un split específico de un evento
   * @param {string} eventCode - Código del evento
   * @param {number} lapNumber - Número de vuelta/split (1-based)
   * @param {number} totalDistance - Distancia total del evento (backup)
   * @returns {number} - Distancia acumulada hasta ese split
   */
  export const getSplitDistance = (eventCode, lapNumber, totalDistance = null) => {
    const distances = getEventSplitDistances(eventCode, totalDistance);
    
    // Ajustar lapNumber a array index (0-based)
    const index = lapNumber - 1;
    
    if (index >= 0 && index < distances.length) {
      return distances[index];
    }
    
    // Si se excede el número de splits configurados, calcular extrapolando
    if (distances.length > 0) {
      const lastDistance = distances[distances.length - 1];
      const interval = distances.length > 1 
        ? distances[distances.length - 1] - distances[distances.length - 2]
        : 400; // Fallback a 400m
      
      return lastDistance + (interval * (index - distances.length + 1));
    }
    
    // Fallback final
    return lapNumber * 400;
  };
  
  /**
   * Calcula el split time para una vuelta específica
   * @param {Array<string>} cumulativeTimes - Tiempos acumulados
   * @param {number} lapIndex - Índice de la vuelta (0-based)
   * @returns {string} - Tiempo del split
   */
  export const calculateSplitTime = (cumulativeTimes, lapIndex) => {
    if (!cumulativeTimes || lapIndex < 0 || lapIndex >= cumulativeTimes.length) {
      return '-';
    }
    
    if (lapIndex === 0) {
      return cumulativeTimes[0];
    }
    
    const currentTime = cumulativeTimes[lapIndex];
    const previousTime = cumulativeTimes[lapIndex - 1];
    
    if (!currentTime || !previousTime) {
      return '-';
    }
    
    // Aquí se podría implementar la resta de tiempos si fuera necesario
    // Por ahora retornamos el tiempo como viene
    return currentTime;
  };
  
  /**
   * Obtiene información completa de eventos soportados
   * @returns {Object} - Objeto con información de todos los eventos configurados
   */
  export const getSupportedEvents = () => {
    return Object.keys(TRACK_DISTANCE_CONFIGS).reduce((acc, eventCode) => {
      acc[eventCode] = {
        distances: TRACK_DISTANCE_CONFIGS[eventCode],
        totalSplits: TRACK_DISTANCE_CONFIGS[eventCode].length,
        maxDistance: Math.max(...TRACK_DISTANCE_CONFIGS[eventCode])
      };
      return acc;
    }, {});
  };
  
  /**
   * Verifica si un evento tiene configuración específica de splits
   * @param {string} eventCode - Código del evento
   * @returns {boolean} - True si tiene configuración específica
   */
  export const hasCustomSplitConfig = (eventCode) => {
    const cleanCode = String(eventCode).trim().toUpperCase();
    return Boolean(TRACK_DISTANCE_CONFIGS[cleanCode]);
  };